import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useMediaQuery } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { LienPriorityType, SourceOfFundsType } from 'constants/loanApplication';
import { SCREEN_WIDTH } from 'constants/ui';
import { checkIsDirectPendingChange } from 'utils/pendingChanges';

import { selectAdditionalMortgageLienPriorityOptions } from 'store/loanApplications/selectors/lienPriorities.selectors';

import { defaultForm, pagesPropTypes } from '../../MainContent.constants';

import { getAdditionalMortgageConfig } from './AdditionalMortgageForm.config';
import {
  COLUMNS_LARGE_SCREEN,
  COLUMNS_SMALL_SCREEN,
  deferredThreshold,
} from './AdditionalMortgageForm.constants';
import { normalizeForm } from './AdditionalMortgageForm.utils';

const AdditionalMortgageForm = ({
  changedFields,
  disabledPendingChangesHighlight,
  form = defaultForm,
  handleChange,
  isSaving,
  isLoading,
  pendingChanges,
  setInForm,
  setForm,
  staticValidateField,
  setValidationErrors,
  validateField,
  validationErrors,
  setEntityConfig,
  onDeleteAdditionalMortgage,
  onRestoreAdditionalMortgage,
  applicationData,
  entitySideData,
  fieldKey,
}) => {
  const lienPriorityOptions = useSelector(selectAdditionalMortgageLienPriorityOptions);

  const applicationLienPriority = form.lienPriority?.value ?? applicationData.lienPriority;

  const [busyLienPriorityTypesMap, setBusyLienPriorityTypesMap] = useState({});

  const numberOfColumns = useMediaQuery(theme => theme.breakpoints.down(SCREEN_WIDTH.XL))
    ? COLUMNS_SMALL_SCREEN
    : COLUMNS_LARGE_SCREEN;

  const mortgageLienPriorityOptions = useMemo(
    () =>
      lienPriorityOptions.filter(({ value }) => {
        if (value === LienPriorityType.OTHER) {
          return true;
        }

        if (value === applicationLienPriority) {
          return false;
        }

        return !Object.entries(busyLienPriorityTypesMap).some(
          ([key, lienPriority]) => lienPriority === value && key !== fieldKey,
        );
      }),
    [busyLienPriorityTypesMap, fieldKey, applicationLienPriority, lienPriorityOptions],
  );

  const mortgages = form.additionalMortgages;
  const mortgageForm = mortgages?.[fieldKey];
  const lienPriorityValue = mortgageForm?.lienPriorityType?.value;
  const isOtherSourceOfFunds = mortgageForm?.sourceOfFunds?.value === SourceOfFundsType.OTHER;
  const isDeferredThreshold = mortgageForm?.monthsDeferred >= deferredThreshold;
  const isHELOC = mortgageForm?.heloc;
  const lienPriorityPendingChange =
    pendingChanges.additionalMortgages?.[fieldKey]?.lienPriorityType;
  const isLienPriorityChanged = checkIsDirectPendingChange(lienPriorityPendingChange);

  const isInitiallyNewAdded =
    !!applicationData.initiallyNewAddedAdditionalMortgagesFieldsKeys?.includes(fieldKey);
  const isNewAdded =
    !!entitySideData.additionalMortgages?.newAddedAdditionalMortgages?.includes(fieldKey);
  const isInitiallyDeleted =
    !!applicationData.initiallyDeletedAdditionalMortgagesFieldsKeys?.includes(fieldKey);
  const isDeleted =
    !!entitySideData.additionalMortgages?.deletedAdditionalMortgages?.includes(fieldKey);

  const isFormDisabled = isDeleted || isInitiallyDeleted;
  const isPendingChangesHighlightDisabled =
    isDeleted ||
    isInitiallyDeleted ||
    isNewAdded ||
    isInitiallyNewAdded ||
    disabledPendingChangesHighlight;

  const onDelete = useCallback(
    () => onDeleteAdditionalMortgage(fieldKey),
    [fieldKey, onDeleteAdditionalMortgage],
  );

  const onRestore = useCallback(() => {
    if (Object.values(busyLienPriorityTypesMap).includes(lienPriorityPendingChange?.initialValue)) {
      setInForm(`additionalMortgages[${fieldKey}].lienPriorityType`, null, true);
    }

    onRestoreAdditionalMortgage(fieldKey);
  }, [
    fieldKey,
    onRestoreAdditionalMortgage,
    busyLienPriorityTypesMap,
    setInForm,
    lienPriorityPendingChange?.initialValue,
  ]);

  const config = useMemo(
    () =>
      getAdditionalMortgageConfig({
        lienPriorityValue,
        mortgageLienPriorityOptions,
        fieldKey,
        numberOfColumns,
        onDelete,
        onRestore,
        isOtherSourceOfFunds,
        isDeferredThreshold,
        isHELOC,
        isNewAdded,
        isDeleted,
        isInitiallyNewAdded,
        isInitiallyDeleted,
        isLienPriorityChanged,
        isLoading,
      }),
    [
      lienPriorityValue,
      mortgageLienPriorityOptions,
      fieldKey,
      numberOfColumns,
      onDelete,
      onRestore,
      isOtherSourceOfFunds,
      isDeferredThreshold,
      isHELOC,
      isNewAdded,
      isDeleted,
      isInitiallyNewAdded,
      isInitiallyDeleted,
      isLienPriorityChanged,
      isLoading,
    ],
  );

  useEffect(() => {
    setEntityConfig(config);
  }, [fieldKey, isNewAdded, config, setEntityConfig]);

  useEffect(() => {
    setForm(form =>
      normalizeForm({
        form,
        fieldKey,
      }),
    );
  }, [config, setForm, fieldKey]);

  useEffect(() => {
    setBusyLienPriorityTypesMap(prev => {
      const updatedBusyLienPriorityTypesMap =
        Object.entries(mortgages ?? {}).reduce((acc, [fieldKey, { lienPriorityType }]) => {
          const isDeleted = !!(
            entitySideData.additionalMortgages?.deletedAdditionalMortgages?.includes(fieldKey) ||
            applicationData.initiallyDeletedAdditionalMortgagesFieldsKeys?.includes(fieldKey)
          );

          if (lienPriorityType?.value === LienPriorityType.OTHER || isDeleted) {
            return acc;
          }

          acc[fieldKey] = lienPriorityType?.value;

          return acc;
        }, {}) ?? [];

      return _.isEqual(prev, updatedBusyLienPriorityTypesMap)
        ? prev
        : updatedBusyLienPriorityTypesMap;
    });
  }, [mortgages, applicationData, entitySideData]);

  useEffect(() => {
    if (!isDeleted) {
      return;
    }

    setValidationErrors(prev => ({
      ...prev,
      additionalMortgages: {
        ...prev.additionalMortgages,
        [fieldKey]: {},
      },
    }));
  }, [setValidationErrors, fieldKey, isDeleted]);

  return (
    <FormGenerator
      isNestedForm
      changedFields={changedFields}
      config={config}
      disabledPendingChangesHighlight={isPendingChangesHighlightDisabled}
      form={form}
      formName={FormNames.ADDITIONAL_FINANCING}
      handleChange={handleChange}
      isDisabled={isFormDisabled}
      isLoading={isSaving || isLoading}
      numberOfColumns={1}
      pendingChanges={pendingChanges}
      setInForm={setInForm}
      staticValidateField={staticValidateField}
      validateField={validateField}
      validationErrors={validationErrors}
    />
  );
};

AdditionalMortgageForm.propTypes = {
  ...pagesPropTypes,
  fieldKey: PropTypes.string.isRequired,
  onDeleteAdditionalMortgage: PropTypes.func.isRequired,
  onRestoreAdditionalMortgage: PropTypes.func.isRequired,
};

export default memo(AdditionalMortgageForm);
